import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  IAlarmConfiguration,
  IDelayConfiguration,
  IMinorDelayConfiguration,
  IProjectedDelayConfiguration,
} from '../types/ISettings';

import { ILine, LinesByContractId } from '@/api/configurations';

interface ConfigurationState {
  alarms: IAlarmConfiguration[];
  delays: IDelayConfiguration[];
  projectedDelays: IProjectedDelayConfiguration[];
  minorDelays: IMinorDelayConfiguration[];
  lines: LinesByContractId;
  loading: boolean;
  error: string | null;
}

const initialState: ConfigurationState = {
  alarms: [],
  delays: [],
  projectedDelays: [],
  minorDelays: [],
  lines: {} as LinesByContractId,
  loading: false,
  error: null,
};

const configurationSlice = createSlice({
  name: 'configurations',
  initialState,
  reducers: {
    setAlarms: (state, action: PayloadAction<IAlarmConfiguration[]>) => {
      state.alarms = action.payload;
    },
    addAlarm: (state, action: PayloadAction<IAlarmConfiguration>) => {
      state.alarms.push(action.payload);
    },
    updateAlarm: (state, action: PayloadAction<IAlarmConfiguration>) => {
      const index = state.alarms.findIndex((a) => a.id === action.payload.id);
      if (index !== -1) {
        state.alarms[index] = action.payload;
      }
    },
    removeAlarm: (state, action: PayloadAction<number>) => {
      state.alarms = state.alarms.filter((a) => a.id !== action.payload);
    },

    setDelays: (state, action: PayloadAction<IDelayConfiguration[]>) => {
      state.delays = action.payload;
    },
    addDelay: (state, action: PayloadAction<IDelayConfiguration>) => {
      state.delays.push(action.payload);
    },
    updateDelay: (state, action: PayloadAction<IDelayConfiguration>) => {
      const index = state.delays.findIndex((d) => d.id === action.payload.id);
      if (index !== -1) {
        state.delays[index] = action.payload;
      }
    },
    removeDelay: (state, action: PayloadAction<number>) => {
      state.delays = state.delays.filter((d) => d.id !== action.payload);
    },

    setProjectedDelays: (
      state,
      action: PayloadAction<IProjectedDelayConfiguration[]>
    ) => {
      state.projectedDelays = action.payload;
    },
    addProjectedDelay: (
      state,
      action: PayloadAction<IProjectedDelayConfiguration>
    ) => {
      state.projectedDelays.push(action.payload);
    },
    updateProjectedDelay: (
      state,
      action: PayloadAction<IProjectedDelayConfiguration>
    ) => {
      const index = state.projectedDelays.findIndex(
        (pd) => pd.id === action.payload.id
      );
      if (index !== -1) {
        state.projectedDelays[index] = action.payload;
      }
    },
    removeProjectedDelay: (state, action: PayloadAction<number>) => {
      state.projectedDelays = state.projectedDelays.filter(
        (pd) => pd.id !== action.payload
      );
    },

    setMinorDelays: (
      state,
      action: PayloadAction<IMinorDelayConfiguration[]>
    ) => {
      state.minorDelays = action.payload;
    },
    addMinorDelay: (state, action: PayloadAction<IMinorDelayConfiguration>) => {
      state.minorDelays.push(action.payload);
    },
    updateMinorDelay: (
      state,
      action: PayloadAction<IMinorDelayConfiguration>
    ) => {
      const index = state.minorDelays.findIndex(
        (md) => md.id === action.payload.id
      );
      if (index !== -1) {
        state.minorDelays[index] = action.payload;
      }
    },
    removeMinorDelay: (state, action: PayloadAction<number>) => {
      state.minorDelays = state.minorDelays.filter(
        (md) => md.id !== action.payload
      );
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setLines: (state, action: PayloadAction<LinesByContractId>) => {
      const lineMapToOptions = (lines: ILine[]) =>
        lines.map((line) => ({
          label: line.lineNumber,
          value: line.lineNumber,
        }));
      state.lines = {
        ...state.lines,
        ...Object.fromEntries(
          Object.entries(action.payload).map(([contractId, lines]) => [
            contractId,
            lineMapToOptions(lines),
          ])
        ),
      };
    },
  },
});

export const {
  setAlarms,
  addAlarm,
  updateAlarm,
  removeAlarm,
  setDelays,
  addDelay,
  updateDelay,
  removeDelay,
  setProjectedDelays,
  addProjectedDelay,
  updateProjectedDelay,
  removeProjectedDelay,
  setMinorDelays,
  addMinorDelay,
  updateMinorDelay,
  removeMinorDelay,
  setLoading,
  setError,
  setLines,
} = configurationSlice.actions;

export default configurationSlice.reducer;
